import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/layout/AppSidebar";
import React, { ReactNode } from "react";
import { useLocation, Link } from "react-router-dom";
import agroHeroBg from "@/assets/agro-hero-bg.jpg";
import solveLogoIcon from "@/assets/solve-logo-icon.png";
import { NotificationDropdown } from "@/components/notifications/NotificationDropdown";
import { UserProfileDropdown } from "@/components/profile/UserProfileDropdown";
import { useProfile } from "@/hooks/useProfile";
import { SolveFloatingAssistant } from "@/components/ai-assistant/SolveFloatingAssistant";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const routeLabels: Record<string, { label: string; parent?: string }> = {
  "/": { label: "Dashboard" },
  "/conversas": { label: "Conversas", parent: "/" },
  "/contatos": { label: "Contatos", parent: "/" },
  "/templates": { label: "Templates", parent: "/" },
  "/disparos": { label: "Disparos", parent: "/" },
  "/follow-up-boletos": { label: "Follow-up", parent: "/" },
  "/automacoes": { label: "Automações", parent: "/" },
  "/boletos": { label: "Base de Boletos", parent: "/" },
  "/robo-conversacional": { label: "Robô Conversacional", parent: "/" },
  "/canhotos": { label: "Central de Canhotos", parent: "/" },
  "/configuracoes": { label: "Configurações", parent: "/" },
  "/campanhas": { label: "Campanhas", parent: "/" },
  "/assinaturas": { label: "Assinaturas", parent: "/" },
  "/cprs": { label: "CPRs", parent: "/" },
  "/garantias": { label: "Garantias", parent: "/" },
  "/radar-agro": { label: "Radar Agro", parent: "/" },
  "/ajuda": { label: "Ajuda", parent: "/" },
  "/notificacoes": { label: "Notificações", parent: "/" },
  "/billing": { label: "Faturamento", parent: "/" },
};

const getBreadcrumbs = (pathname: string) => {
  const crumbs: { path: string; label: string }[] = [];
  let currentPath = pathname;

  while (currentPath) {
    const route = routeLabels[currentPath];
    if (route) {
      crumbs.unshift({ path: currentPath, label: route.label });
      currentPath = route.parent || "";
    } else {
      break;
    }
  }

  return crumbs;
};

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { profile } = useProfile();
  const location = useLocation();
  const breadcrumbs = getBreadcrumbs(location.pathname);
  
  return (
    <SidebarProvider>
      <div className="min-h-screen w-full bg-gradient-to-br from-background via-background to-muted/20 relative flex flex-col">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url(${agroHeroBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/90 to-background/95" />
        
        {/* Header - Full width at the top */}
        <header className="h-16 w-full bg-card/95 backdrop-blur-md flex items-center px-4 sticky top-0 z-50 shadow-sm relative">
          {/* Green gradient bottom border */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
          
          {/* Left section: Menu trigger + Breadcrumb */}
          <div className="flex items-center">
            <SidebarTrigger className="hover:bg-accent transition-colors" />
            
            {/* Breadcrumb */}
            <div className="hidden sm:flex items-center ml-4">
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={crumb.path}>
                      {index > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem>
                        {index === breadcrumbs.length - 1 ? (
                          <BreadcrumbPage className="font-medium">{crumb.label}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <Link to={crumb.path} className="text-muted-foreground hover:text-foreground transition-colors">
                              {crumb.label}
                            </Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </div>
          
          {/* Center: Logo + Slogan */}
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="flex items-center gap-2">
              {/* Logo icon */}
              <img 
                src={solveLogoIcon} 
                alt="Solve.AI Logo" 
                className="h-9 w-9 object-contain"
              />
              {/* Logo text with gradient */}
              <span className="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground via-foreground to-primary bg-clip-text text-transparent">
                SOLVE
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
                .AI
              </span>
            </div>
            <span className="text-[10px] text-muted-foreground tracking-wide hidden sm:block mt-0.5">
              A inteligência que cultiva resultados
            </span>
          </div>
          
          {/* Right section: Actions */}
          <div className="flex items-center gap-3">
            <NotificationDropdown />
            <Separator orientation="vertical" className="h-6 hidden sm:block" />
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                {profile?.full_name?.split(' ')[0] || 'Usuário'}
              </span>
            </div>
            <UserProfileDropdown />
          </div>
        </header>

        {/* Content Area: Sidebar + Main */}
        <div className="flex flex-1 overflow-hidden relative z-10">
          {/* Sidebar */}
          <AppSidebar />
          
          {/* Main Content with proper spacing */}
          <main className="flex-1 overflow-y-auto p-8">
            <div className="max-w-7xl mx-auto w-full animate-fade-in">
              {children}
            </div>
          </main>
        </div>

        {/* Solve Floating Assistant */}
        <SolveFloatingAssistant currentPage={location.pathname} />
      </div>
    </SidebarProvider>
  );
}